import { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, Trash2, Shield, Eye, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const roleLabel = { admin: "Administrateur", editor: "Lecture-écriture", viewer: "Lecture seule" };
const roleStyle = {
  admin: "bg-[#4F46E5] text-white",
  editor: "bg-[#4ADE80] text-slate-900",
  viewer: "bg-[#FBBF24] text-slate-900",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      console.error("Failed to load users (non-admin or error):", e);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "admin") {
    return (
      <div className="ml-64 p-8 min-h-screen flex items-center justify-center">
        <div className="nb-card p-10 text-center max-w-md" data-testid="users-access-denied">
          <h2 className="font-heading font-black text-2xl mb-2">Accès refusé</h2>
          <p className="text-slate-500 font-medium">Seul l'administrateur peut gérer les utilisateurs.</p>
        </div>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utilisateur créé");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "viewer" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      await load();
      toast.success("Rôle mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte ${u.email} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      await load();
      toast.success("Compte supprimé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const inputCls = "nb-input w-full px-3 py-2.5 font-medium text-sm";

  return (
    <div className="ml-64 p-8 min-h-screen">
      <div className="flex items-center justify-between mb-8 mc-slide-up">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Utilisateurs</h1>
          <p className="text-slate-500 font-medium mt-1">Gérez les accès (lecture seule / lecture-écriture)</p>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#4F46E5] text-white font-bold px-5 py-3 rounded-xl nb-btn"
        >
          <Plus strokeWidth={2.5} size={20} /> Nouvel utilisateur
        </button>
      </div>

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="nb-card p-5 flex flex-wrap items-center justify-between gap-4 mc-pop">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl border-2 border-slate-900 flex items-center justify-center bg-[#FDFBFE]">
                {u.role === "admin" ? <Shield size={20} strokeWidth={2.5} /> : u.role === "editor" ? <Pencil size={20} strokeWidth={2.5} /> : <Eye size={20} strokeWidth={2.5} />}
              </div>
              <div>
                <p className="font-heading font-bold">{u.name} {u.is_owner && <span className="text-xs text-slate-400">(propriétaire)</span>}</p>
                <p className="text-sm text-slate-500 font-medium">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {u.is_owner ? (
                <span className={`text-xs font-bold px-3 py-1.5 rounded-lg border-2 border-slate-900 ${roleStyle[u.role]}`}>
                  {roleLabel[u.role]}
                </span>
              ) : (
                <select
                  data-testid={`user-role-${u.id}`}
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className="nb-input px-3 py-2 font-bold text-sm"
                >
                  <option value="viewer">Lecture seule</option>
                  <option value="editor">Lecture-écriture</option>
                </select>
              )}
              {!u.is_owner && (
                <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u)} className="text-red-600 hover:scale-110 transition-transform">
                  <Trash2 size={20} strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="nb-card w-full max-w-md p-6 mc-pop" data-testid="user-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-black text-2xl">Nouvel utilisateur</h2>
              <button onClick={() => setShowForm(false)} className="w-9 h-9 rounded-lg nb-btn bg-white flex items-center justify-center">
                <X strokeWidth={2.5} size={18} />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required type="email" data-testid="user-email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              <input data-testid="user-name" className={inputCls} placeholder="Nom" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              <input required type="password" data-testid="user-password" className={inputCls} placeholder="Mot de passe" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
              <select data-testid="user-role-select" className={inputCls} value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
                <option value="viewer">Lecture seule</option>
                <option value="editor">Lecture-écriture</option>
              </select>
              <button type="submit" disabled={saving} data-testid="user-submit" className="w-full bg-[#4F46E5] text-white font-bold py-3 rounded-xl nb-btn disabled:opacity-60">
                {saving ? "Création..." : "Créer le compte"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
